import { useRef, useState } from "react";
import {
  Box,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Icon,
  Input,
  Select,
  Switch,
  Text,
  VStack,
} from "@chakra-ui/react";
import { MdFilterAlt, MdInfoOutline, MdMarkEmailUnread, MdSchedule } from "react-icons/md";
import {
  CONTEXTO_LABEL,
  CatalogoDisparo,
  CombinadorFiltro,
  ContextoDisparo,
  DESTINATARIO_LABEL,
  DestinatarioDisparo,
  DIAS_SEMANA,
  FREQUENCIA_LABEL,
  FrequenciaDisparo,
  filtroEstaCompleto,
  PreviewBuscaDto,
  PreviewResultado,
  TIPO_GATILHO_LABEL,
  TipoGatilho,
} from "@/models/consignado/disparo.model";
import {
  CanalDraft,
  DisparoDraft,
  DisparoDraftPatch,
  DisparoErros,
  FiltroDraft,
  limparVariaveisOrfas,
  semVariaveis,
  gatilhoUsaFiltro,
  semEventoNoContexto,
} from "./disparo-draft";
import {
  CondicaoNo,
  GrupoNo,
  adicionarCondicao,
  adicionarSubgrupo,
  arvoreEstaCompleta,
  atualizarCombinador,
  atualizarCondicao,
  condicaoComCampo,
  condicoesDaArvore,
  encontrarCondicao,
  novoGrupo,
  removerNo,
} from "./filtro-arvore";
import {
  camposDaFrequencia,
  canaisDisponiveis as canaisDoCatalogo,
  evento as buscarEvento,
  eventosDoContexto,
  frequenciaSpec,
} from "./catalogo.helpers";
import SectionCard from "./SectionCard";
import CanalCard from "./CanalCard";
import CampoLabel from "./CampoLabel";
import FiltroGrupoView from "./FiltroGrupoView";
import TestarBuscaModal from "./TestarBuscaModal";
import { FiltroPopoverContext } from "./FiltroPopoverContext";
import { chaveNovaCondicao, chaveNovoSubgrupo, useFiltroActive } from "./FiltroActiveContext";
import { fieldFont, labelStyles } from "./styleTokens";

type Props = {
  value: DisparoDraft;
  erros: DisparoErros;
  catalogo?: CatalogoDisparo;
  onChange: (patch: DisparoDraftPatch) => void;
  // Colapso de "Identificação do disparo" — levantado pro componente pai
  // porque também decide o que a sidebar mostra: com essa seção aberta é a
  // sessão do disparo (campo de filtro), fechada é a da mensagem (variáveis).
  identificacaoAberta: boolean;
  onToggleIdentificacao: () => void;
  /** Foco entrou em "Canal e mensagem" — fecha a identificação. */
  onEditarCanais: () => void;
  onInserirToken: (token: string) => void;
  /** POST /disparos/preview; ausente, o "Testar busca" simula a amostra. */
  onPreview?: (dto: PreviewBuscaDto) => Promise<PreviewResultado>;
};


export default function DisparoForm({
  value,
  erros,
  catalogo,
  onChange,
  identificacaoAberta,
  onToggleIdentificacao,
  onEditarCanais,
  onInserirToken,
  onPreview,
}: Props) {
  // Só um popover de filtro aberto por vez (ver FiltroPopoverContext).
  const [openFiltroId, setOpenFiltroId] = useState<string | null>(null);
  const [erroFiltroId, setErroFiltroId] = useState<string | null>(null);
  // Aviso de que a troca de contexto zerou o filtro; some quando o filtro
  // ganha a primeira condição de novo.
  const [filtroZerado, setFiltroZerado] = useState(false);
  // Idem para a mensagem: a troca de contexto tirou as variáveis dela.
  const [variaveisZeradas, setVariaveisZeradas] = useState(false);
  // Clique num botão da seção da mensagem: a identificação só recolhe no
  // click. Recolher no foco (que vem no mousedown) subia a tela antes do
  // mouseup, e o clique caía fora do botão — "Adicionar" e "Editar template"
  // precisavam de dois cliques.
  const cliqueEmBotao = useRef(false);
  const { registerActiveFiltro, clearActiveFiltro, activeKey } = useFiltroActive();

  /**
   * Só deixa fechar o popover aberto (id === null) ou trocar para outro se o
   * filtro aberto já tiver valor. Senão recusa e sinaliza o erro nele, em vez
   * de deixar o usuário abandonar uma condição pela metade.
   *
   * A checagem é ao vivo (`filtroEstaCompleto`), não pelo mapa de erros: esse
   * só é preenchido ao salvar, e aí o bloqueio nunca agiria durante a edição.
   */
  const requestOpenFiltroId = (id: string | null): boolean => {
    if (openFiltroId && openFiltroId !== id) {
      const aberto = encontrarCondicao(value.filtros, openFiltroId);
      if (aberto && (!aberto.propriedade || !filtroEstaCompleto(aberto))) {
        setErroFiltroId(openFiltroId);
        return false;
      }
    }
    setErroFiltroId(null);
    setOpenFiltroId(id);
    return true;
  };

  /**
   * Mesmo critério para o "Testar busca": com uma condição pela metade a
   * query ainda não está montada.
   *
   * Lista vazia também bloqueia. O `PreviewBuscaDto` deixou de exigir ao menos
   * uma condição, e `normalizarFiltros([])` vira um grupo E vazio, que o
   * `montarWhereArvore` resolve como `WHERE (TRUE)` — a prévia devolveria
   * registros quaisquer do contexto e passaria por amostra do público. É só
   * leitura com LIMIT, mas a amostra seria mentirosa.
   */
  const usaFiltro = gatilhoUsaFiltro(value.tipoGatilho);
  const condicoesGuardadas = condicoesDaArvore(value.filtros).length;
  const condicoes = condicoesDaArvore(value.filtros);
  const filtrosIncompletos = condicoes.length === 0 || !arvoreEstaCompleta(value.filtros);

  const eventos = eventosDoContexto(catalogo, value.contexto);
  const canaisPossiveis = canaisDoCatalogo(catalogo);
  const canaisUsados = value.canais.map((c) => c.canal);
  const spec = frequenciaSpec(catalogo, value.frequencia);
  const campos = camposDaFrequencia(catalogo, value.frequencia);

  /**
   * O contexto é a raiz do filtro e das variáveis: os campos, os status e as
   * entidades de cada um são próprios. Por isso a troca **zera o filtro** e
   * **tira as variáveis da mensagem** (declaração e `{{token}}`, e o vínculo
   * com template, que é do contexto anterior — ver semVariaveis), com aviso
   * nas duas seções. Contato e evento ficam como estão, e o que não existir no
   * contexto novo é apontado na hora (validarContexto, no DisparoScreen).
   */
  const handleContexto = (contexto: ContextoDisparo) => {
    if (contexto === value.contexto) return;
    // Popover, erro de "pela metade" e seletor de campo pertenciam a
    // condições que deixam de existir.
    setOpenFiltroId(null);
    setErroFiltroId(null);
    if (activeKey) clearActiveFiltro(activeKey);
    setFiltroZerado(condicoesDaArvore(value.filtros).length > 0);
    setVariaveisZeradas(value.canais.some((c) => c.variaveis.length > 0 || !!c.templateId));
    onChange((prev) => ({
      contexto,
      filtros: novoGrupo(CombinadorFiltro.E),
      canais: prev.canais.map(semVariaveis),
    }));
  };

  const handleDestinatario = (destinatario: DestinatarioDisparo) => onChange({ destinatario });

  const handleTipoGatilho = (tipoGatilho: TipoGatilho) =>
    onChange({
      tipoGatilho,
      evento: "",
      ...(tipoGatilho === TipoGatilho.ROTINA
        ? {}
        : { frequencia: null, intervalo: null, hora: null, diaSemana: null, diaMes: null }),
    });

  const handleFrequencia = (frequencia: FrequenciaDisparo | null) => {
    // Cada frequência declara os campos que aceita — os demais precisam
    // sair do draft, porque o backend recusa campo incompatível.
    const permitidos = camposDaFrequencia(catalogo, frequencia);
    onChange({
      frequencia,
      intervalo: permitidos.includes("intervalo") ? value.intervalo : null,
      hora: permitidos.includes("hora") ? value.hora ?? "09:00" : null,
      diaSemana: permitidos.includes("diaSemana") ? value.diaSemana ?? 1 : null,
      diaMes: permitidos.includes("diaMes") ? value.diaMes ?? 1 : null,
    });
  };

  const updateFiltro = (id: string, patch: Partial<FiltroDraft>) =>
    onChange((prev) => ({ filtros: atualizarCondicao(prev.filtros, id, patch) }));

  // Criar condição ou subgrupo abandona a condição aberta, então passa pelo
  // mesmo pedido que clicar noutra pill: se a aberta está pela metade, a
  // troca é recusada e o erro aparece nela.
  //
  // O "+" e o "( )" não criam nada ainda: abrem o seletor de campo na sidebar,
  // e a condição só nasce quando o campo é escolhido — já com ele e com o
  // popover de operador/valor aberto. Antes nascia uma pill vazia "Escolha o
  // campo" no meio do caminho, uma etapa a mais.
  const escolherCampoParaNova = (
    chave: string,
    grupoId: string,
    adicionar: (raiz: GrupoNo, grupoId: string, condicao: CondicaoNo) => GrupoNo,
  ) => {
    // Segundo clique no botão destacado desiste da escolha.
    if (activeKey === chave) {
      clearActiveFiltro(chave);
      return;
    }
    if (!requestOpenFiltroId(null)) return;
    registerActiveFiltro(chave, (p) => {
      const condicao = condicaoComCampo(p);
      onChange((prev) => ({ filtros: adicionar(prev.filtros, grupoId, condicao) }));
      setOpenFiltroId(condicao.id);
    });
  };

  const addCondicao = (grupoId: string) =>
    escolherCampoParaNova(chaveNovaCondicao(grupoId), grupoId, adicionarCondicao);
  const addSubgrupo = (grupoId: string) =>
    escolherCampoParaNova(chaveNovoSubgrupo(grupoId), grupoId, adicionarSubgrupo);

  // A limpeza roda a cada alteração do canal porque o mapa de variáveis não é
  // mais editável: apagar `{{apelido}}` do texto é o único jeito de descartar
  // a variável, e ela precisa sair junto.
  const updateCanal = (id: string, patch: Partial<CanalDraft>) =>
    onChange((prev) => ({
      canais: prev.canais.map((c) => (c.id === id ? limparVariaveisOrfas({ ...c, ...patch }) : c)),
    }));

  const eventoSelecionado = eventos.find((e) => e.id === value.evento);
  // Escolhido num contexto anterior e sem lugar no atual: vira opção
  // desabilitada, senão o Select mostraria em branco um valor que existe.
  const eventoForaDoContexto =
    value.evento && !eventoSelecionado ? buscarEvento(catalogo, value.evento) : undefined;
  // Contexto que nenhum evento aciona (hoje, Cobrança CLT). Sem catálogo
  // carregado não dá para afirmar isso.
  const semEventos = !!catalogo && eventos.length === 0;

  return (
    <VStack spacing={5} align="stretch">
      <SectionCard
        title="Identificação do disparo"
        icon={MdInfoOutline}
        isOpen={identificacaoAberta}
        onToggle={onToggleIdentificacao}
      >
        <VStack spacing={5} align="stretch">
          <FormControl isInvalid={!!erros.nome} isRequired>
            <CampoLabel ajuda="Como o disparo aparece na grade.">Nome do disparo</CampoLabel>
            <Input
              {...fieldFont}
              placeholder="Ex.: Avisar tomador quando a proposta é paga"
              value={value.nome}
              onChange={(e) => onChange({ nome: e.target.value })}
            />
            <FormErrorMessage {...fieldFont}>{erros.nome}</FormErrorMessage>
          </FormControl>

          <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4}>
            <GridItem>
              <FormControl isRequired>
                <CampoLabel ajuda="A entidade que o disparo observa. Define quais registros o filtro alcança.">Contexto</CampoLabel>
                <Select
                  {...fieldFont}
                  value={value.contexto}
                  onChange={(e) => handleContexto(e.target.value as ContextoDisparo)}
                >
                  {(catalogo?.entidades ?? []).map((e) => (
                    <option key={e.id} value={e.id}>
                      {CONTEXTO_LABEL[e.id] ?? e.descricao}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl isRequired>
                <CampoLabel ajuda="Quem recebe a mensagem.">Destinatário</CampoLabel>
                <Select
                  {...fieldFont}
                  value={value.destinatario}
                  onChange={(e) => handleDestinatario(e.target.value as DestinatarioDisparo)}
                >
                  {Object.values(DestinatarioDisparo).map((d) => (
                    <option key={d} value={d}>
                      {DESTINATARIO_LABEL[d]}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl isRequired>
                <CampoLabel ajuda="O que aciona o disparo: um fato do sistema ou uma varredura periódica.">Gatilho</CampoLabel>
                <Select
                  {...fieldFont}
                  value={value.tipoGatilho}
                  onChange={(e) => handleTipoGatilho(e.target.value as TipoGatilho)}
                >
                  {Object.values(TipoGatilho)
                    // Manual fica oculto (decisão do tech lead). Disparo já
                    // gravado como manual continua mostrando o próprio gatilho.
                    .filter((t) => t !== TipoGatilho.MANUAL || value.tipoGatilho === TipoGatilho.MANUAL)
                    .map((t) => {
                      const eventoIndisponivel = t === TipoGatilho.EVENTO && semEventos;
                      return (
                        <option
                          key={t}
                          value={t}
                          // Continua escolhível se já for o gatilho do rascunho,
                          // para o Select não ficar mostrando outra coisa.
                          disabled={eventoIndisponivel && value.tipoGatilho !== t}
                        >
                          {TIPO_GATILHO_LABEL[t]}
                          {eventoIndisponivel ? " (nenhum neste contexto)" : ""}
                        </option>
                      );
                    })}
                </Select>
              </FormControl>
            </GridItem>
          </Grid>

          {value.tipoGatilho === TipoGatilho.EVENTO && (
            <FormControl isInvalid={!!erros.evento} isRequired>
              <CampoLabel ajuda="O fato que aciona o disparo. A lista traz só os válidos para o contexto.">Evento</CampoLabel>
              <Select
                {...fieldFont}
                placeholder={semEventos ? "Nenhum evento neste contexto" : "Selecione o evento"}
                value={value.evento}
                isDisabled={semEventos}
                onChange={(e) => onChange({ evento: e.target.value })}
              >
                {eventoForaDoContexto && (
                  <option value={eventoForaDoContexto.id} disabled>
                    {eventoForaDoContexto.descricao} (fora do contexto)
                  </option>
                )}
                {eventos.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.descricao}
                    {ev.definicaoPendente ? " (definição pendente)" : ""}
                  </option>
                ))}
              </Select>
              {semEventos && !erros.evento && (
                <Text fontSize="xs" color="gray.600" mt={1} {...fieldFont}>
                  {semEventoNoContexto(value.contexto, catalogo)}
                </Text>
              )}
              {eventoSelecionado?.definicaoPendente && !erros.evento && (
                <Text fontSize="xs" color="orange.600" mt={1} {...fieldFont}>
                  Evento ainda sem definição fechada — o cadastro só pode ser salvo inativo.
                </Text>
              )}
              <FormErrorMessage {...fieldFont}>{erros.evento}</FormErrorMessage>
            </FormControl>
          )}

          {value.tipoGatilho === TipoGatilho.MANUAL && (
            <Text fontSize="xs" color="gray.500" {...fieldFont}>
              Disparo manual: roda sob demanda, sobre os registros que os filtros abaixo selecionarem.
            </Text>
          )}

          {/* Filtro guardado num disparo por evento (de antes da troca de gatilho,
              ou gravado quando o evento ainda filtrava): não vai no salvar. */}
          {!usaFiltro && condicoesGuardadas > 0 && (
            <Text fontSize="xs" color="orange.600" {...fieldFont}>
              Este disparo tem {condicoesGuardadas}{" "}
              {condicoesGuardadas === 1 ? "condição de filtro" : "condições de filtro"}. Disparo por
              evento não usa filtro: ao salvar, o filtro é removido.
            </Text>
          )}

          {/* --- Filtro (só rotina) --- */}
          {usaFiltro && (
            <>
              <Divider />

              <Flex align="center" gap={2}>
                <Icon as={MdFilterAlt} boxSize={4} color="secondary" />
                <Text fontWeight="bold" fontSize="sm" color="gray.800" fontFamily="Lexend Deca">
                  Filtro
                </Text>
              </Flex>

              <Box>
                <Text
                  fontSize="xs"
                  color={erros.filtros ? "red.500" : "gray.500"}
                  mb={2}
                  {...fieldFont}
                >
                  {erros.filtros ??
                    `Obrigatório: ${TIPO_GATILHO_LABEL[value.tipoGatilho].toLowerCase()} precisa de ao menos um filtro.`}
                </Text>
                {filtroZerado && condicoes.length === 0 && (
                  <Flex align="center" gap={1.5} bg="orange.50" borderRadius="md" px={3} py={2} mb={2}>
                    <Icon as={MdInfoOutline} boxSize={3.5} color="orange.500" flexShrink={0} />
                    <Text fontSize="xs" color="orange.700" {...fieldFont}>
                      O filtro foi zerado porque o contexto mudou — os campos e os status são de cada
                      contexto. Monte o filtro de novo.
                    </Text>
                  </Flex>
                )}
                <FiltroPopoverContext.Provider
                  value={{
                    openId: openFiltroId,
                    setOpenId: setOpenFiltroId,
                    requestOpenId: requestOpenFiltroId,
                    erroId: erroFiltroId,
                  }}
                >
                  <FiltroGrupoView
                    grupo={value.filtros}
                    arvore={value.filtros}
                    contexto={value.contexto}
                    catalogo={catalogo}
                    erros={erros.porFiltro}
                    onAddCondicao={addCondicao}
                    onAddSubgrupo={addSubgrupo}
                    onUpdateCondicao={updateFiltro}
                    onUpdateCombinador={(grupoId, combinador) =>
                      onChange((prev) => ({
                        filtros: atualizarCombinador(prev.filtros, grupoId, combinador),
                      }))
                    }
                    onRemoveNo={(id) => onChange((prev) => ({ filtros: removerNo(prev.filtros, id) }))}
                  />
                </FiltroPopoverContext.Provider>

                {condicoes.length > 1 && (
                  <Text fontSize="xs" color="gray.500" mt={2} {...fieldFont}>
                    Clique no <strong>{CombinadorFiltro.E}</strong>/<strong>{CombinadorFiltro.OU}</strong>{" "}
                    para trocar o combinador do grupo. O botão <strong>( )</strong> abre um subgrupo — o
                    parêntese, que nasce com o combinador oposto ao do pai.
                  </Text>
                )}
              </Box>

              <TestarBuscaModal
                contexto={value.contexto}
                filtros={value.filtros}
                catalogo={catalogo}
                disabled={filtrosIncompletos || Object.keys(erros.porFiltro).length > 0}
                onPreview={onPreview}
              />
            </>
          )}

          {/* --- Agendamento (só ROTINA) --- */}
          {value.tipoGatilho === TipoGatilho.ROTINA && (
            <>
              <Divider />

              <Flex align="center" gap={2}>
                <Icon as={MdSchedule} boxSize={4} color="secondary" />
                <Text fontWeight="bold" fontSize="sm" color="gray.800" fontFamily="Lexend Deca">
                  Agendamento
                </Text>
                {catalogo && (
                  <Text fontSize="xs" color="gray.500" {...fieldFont}>
                    ({catalogo.agendamentos.timezone})
                  </Text>
                )}
              </Flex>

              <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                <GridItem>
                  <FormControl isRequired isInvalid={!!erros.frequencia}>
                    <CampoLabel ajuda="De quanto em quanto tempo a varredura roda.">Frequência</CampoLabel>
                    <Select
                      {...fieldFont}
                      placeholder="Selecione"
                      value={value.frequencia ?? ""}
                      onChange={(e) =>
                        handleFrequencia((e.target.value || null) as FrequenciaDisparo | null)
                      }
                    >
                      {(catalogo?.agendamentos.frequencias ?? []).map((f) => (
                        <option key={f.id} value={f.id}>
                          {FREQUENCIA_LABEL[f.id]}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage {...fieldFont}>{erros.frequencia}</FormErrorMessage>
                  </FormControl>
                </GridItem>

                {campos.includes("intervalo") && (
                  <GridItem>
                    <FormControl isRequired isInvalid={!!erros.intervalo}>
                      <CampoLabel ajuda="De quantos em quantos minutos ou horas a varredura roda.">Intervalo</CampoLabel>
                      <Select
                        {...fieldFont}
                        placeholder="Selecione"
                        value={value.intervalo ?? ""}
                        onChange={(e) => onChange({ intervalo: Number(e.target.value) || null })}
                      >
                        {(spec?.intervalos ?? []).map((i) => (
                          <option key={i} value={i}>
                            {value.frequencia === FrequenciaDisparo.MINUTOS
                              ? `${i} min`
                              : `${i}h`}
                          </option>
                        ))}
                      </Select>
                      <FormErrorMessage {...fieldFont}>{erros.intervalo}</FormErrorMessage>
                    </FormControl>
                  </GridItem>
                )}

                {campos.includes("hora") && (
                  <GridItem>
                    <FormControl isRequired isInvalid={!!erros.hora}>
                      <CampoLabel ajuda="Hora em que a varredura roda.">Horário</CampoLabel>
                      <Input
                        {...fieldFont}
                        type="time"
                        value={value.hora ?? "09:00"}
                        onChange={(e) => onChange({ hora: e.target.value })}
                      />
                      <FormErrorMessage {...fieldFont}>{erros.hora}</FormErrorMessage>
                    </FormControl>
                  </GridItem>
                )}

                {campos.includes("diaSemana") && (
                  <GridItem>
                    <FormControl isRequired isInvalid={!!erros.diaSemana}>
                      <CampoLabel ajuda="Dia da semana em que a varredura roda.">Dia da semana</CampoLabel>
                      <Select
                        {...fieldFont}
                        value={value.diaSemana ?? 1}
                        onChange={(e) => onChange({ diaSemana: Number(e.target.value) })}
                      >
                        {DIAS_SEMANA.filter(
                          (d) => !spec?.diasSemana || spec.diasSemana.includes(d.valor),
                        ).map((d) => (
                          <option key={d.valor} value={d.valor}>
                            {d.label}
                          </option>
                        ))}
                      </Select>
                      <FormErrorMessage {...fieldFont}>{erros.diaSemana}</FormErrorMessage>
                    </FormControl>
                  </GridItem>
                )}

                {campos.includes("diaMes") && (
                  <GridItem>
                    <FormControl isRequired isInvalid={!!erros.diaMes}>
                      <CampoLabel ajuda="Dia do mês em que a varredura roda. Vai até 28, para cair em todo mês.">Dia do mês</CampoLabel>
                      <Select
                        {...fieldFont}
                        value={value.diaMes ?? 1}
                        onChange={(e) => onChange({ diaMes: Number(e.target.value) })}
                      >
                        {Array.from(
                          { length: (spec?.diaMesMaximo ?? 28) - (spec?.diaMesMinimo ?? 1) + 1 },
                          (_, i) => i + (spec?.diaMesMinimo ?? 1),
                        ).map((dia) => (
                          <option key={dia} value={dia}>
                            Dia {dia}
                          </option>
                        ))}
                      </Select>
                      <FormErrorMessage {...fieldFont}>{erros.diaMes}</FormErrorMessage>
                    </FormControl>
                  </GridItem>
                )}
              </Grid>

              {catalogo && (
                <Text fontSize="xs" color="gray.500" {...fieldFont}>
                  {catalogo.agendamentos.observacao}
                </Text>
              )}
            </>
          )}

          <Divider />

          <FormControl display="flex" alignItems="center" gap={3}>
            <Switch
              colorScheme="green"
              isChecked={value.ativo}
              onChange={(e) => onChange({ ativo: e.target.checked })}
            />
            <FormLabel {...labelStyles} mb={0}>
              Ativo
            </FormLabel>
          </FormControl>
        </VStack>
      </SectionCard>

      <SectionCard title="Canal e mensagem" icon={MdMarkEmailUnread}>
        {/* Qualquer campo desta seção focado (canal, remetente, mensagem)
            fecha a identificação — é a passagem para a sessão da mensagem. */}
        <VStack
          spacing={4}
          align="stretch"
          onPointerDownCapture={(e) => {
            cliqueEmBotao.current = !!(e.target as HTMLElement).closest("button");
          }}
          onFocusCapture={() => {
            if (!cliqueEmBotao.current) onEditarCanais();
          }}
          onClickCapture={() => {
            if (!cliqueEmBotao.current) return;
            cliqueEmBotao.current = false;
            onEditarCanais();
          }}
        >
          <Text fontSize="xs" color={erros.canais ? "red.500" : "gray.500"} {...fieldFont}>
            {erros.canais ?? "Um canal por disparo. Para avisar pelo mesmo motivo em outro canal, crie outro disparo."}
          </Text>
          {variaveisZeradas && value.canais.every((c) => c.variaveis.length === 0) && (
            <Flex align="center" gap={1.5} bg="orange.50" borderRadius="md" px={3} py={2}>
              <Icon as={MdInfoOutline} boxSize={3.5} color="orange.500" flexShrink={0} />
              <Text fontSize="xs" color="orange.700" {...fieldFont}>
                As variáveis saíram da mensagem porque o contexto mudou — os campos são de cada
                contexto. Insira de novo pela barra lateral.
              </Text>
            </Flex>
          )}

          {value.canais.map((canal) => (
            <CanalCard
              key={canal.id}
              canal={canal}
              contexto={value.contexto}
              destinatario={value.destinatario}
              catalogo={catalogo}
              erro={erros.porCanal[canal.id]}
              canaisIndisponiveis={canaisUsados}
              canaisDisponiveis={canaisPossiveis}
              onChange={(patch) => updateCanal(canal.id, patch)}
              onRemove={
                value.canais.length > 1
                  ? () =>
                      onChange((prev) => ({
                        canais: prev.canais.filter((c) => c.id !== canal.id),
                      }))
                  : undefined
              }
              onInserirToken={onInserirToken}
            />
          ))}

        </VStack>
      </SectionCard>
    </VStack>
  );
}
